import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getPublicThread,
  getPublicMessages,
  postPublicMessage,
  markPublicRead,
  getPublicAttachmentUrl,
} from "@/lib/chat";
import { connectPublicChatSocket } from "@/lib/chat_ws";

const unwrap = (res) => {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
};

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.value)) return payload.value; // paginatorul tău
  if (payload && Array.isArray(payload.results)) return payload.results;
  if (payload && Array.isArray(payload.items)) return payload.items;
  return [];
};

const isAbsoluteUrl = (s) => /^https?:\/\//i.test(String(s || ""));

export default function PublicChatPage() {
  const { token } = useParams();

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");

  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [newMsgCount, setNewMsgCount] = useState(0);

  const listRef = useRef(null);
  const bottomRef = useRef(null);

  // polling fără restart la fiecare mesaj
  const tokenRef = useRef(token);
  const messagesRef = useRef([]);
  const lastCreatedAtRef = useRef(null);

  // dacă utilizatorul e "jos", auto-scroll; dacă e sus, NU (și arătăm toast)
  const shouldAutoScrollRef = useRef(true);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    messagesRef.current = messages;
    const last = messages[messages.length - 1];
    lastCreatedAtRef.current = last?.created_at || null;
  }, [messages]);

  const biz = useMemo(() => thread?.business || {}, [thread]);
  const appt = useMemo(() => thread?.appointment || {}, [thread]);

  const scrollToBottom = (behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
  };

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    const threshold = 80; // px
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= threshold;
  };

  const onScrollMessages = () => {
    const near = isNearBottom();
    shouldAutoScrollRef.current = near;
    if (near) setNewMsgCount(0);
  };

  const getAttUrl = (att) => {
    const u = att?.url || att?.href || att?.download_url;
    if (u && isAbsoluteUrl(u)) return u;
    // fallback: endpoint public attachment
    if (att?.id && token) return getPublicAttachmentUrl(token, att.id);
    return u || "";
  };

  // previews pentru fișiere selectate (cleanup)
  const previews = useMemo(() => {
    return (pendingFiles || []).map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      name: f?.name || "image",
    }));
  }, [pendingFiles]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch { }
      });
    };
  }, [previews]);

  const addFiles = (filesList) => {
    const incoming = Array.from(filesList || [])
      .filter(Boolean)
      .filter((f) => String(f.type || "").toLowerCase().startsWith("image/"));

    if (incoming.length === 0) return;

    // limită simplă (ca în backend): max 5
    setPendingFiles((prev) => {
      const merged = [...(prev || []), ...incoming];
      return merged.slice(0, 5);
    });
  };

  const removePendingFile = (idx) => {
    setPendingFiles((prev) => (prev || []).filter((_, i) => i !== idx));
  };

  // LOAD initial (thread + mesaje)
  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      setNewMsgCount(0);

      try {
        const t = unwrap(await getPublicThread(token));
        const m = normalizeList(unwrap(await getPublicMessages(token, { limit: 50 })));

        if (!mounted) return;

        setThread(t || null);
        setMessages(m);

        // după load, vrem să fim jos
        shouldAutoScrollRef.current = true;
        setTimeout(() => scrollToBottom("auto"), 0);

        // best-effort mark read
        markPublicRead(token).catch(() => { });
      } catch (e) {
        if (!mounted) return;
        setThread(null);
        setMessages([]);
        setError("Chat indisponibil (token invalid sau expirat).");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [token]);
  // REALTIME (WebSocket)
  useEffect(() => {
    const t = tokenRef.current;
    if (!t) return;

    const sock = connectPublicChatSocket({
      token: t,
      onEvent: (ev) => {
        if (!ev || typeof ev !== "object") return;

        if (ev.type === "chat.message") {
          const msg = ev?.message_public || ev?.message;
          if (!msg || !msg.id) return;

          // dedupe by id
          setMessages((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            if (arr.some((x) => x?.id === msg.id)) return arr;
            return [...arr, msg];
          });

          try {
            const dt = msg?.created_at || msg?.createdAt;
            if (dt) lastCreatedAtRef.current = dt;
          } catch {}

          // best-effort mark read (client)
          markPublicRead(t).catch(() => {});
        }
      },
    });

    return () => {
      try {
        sock?.close?.();
      } catch {}
    };
  }, [token]);
async function onSend(e) {
    e.preventDefault();

    const text = (body || "").trim();
    const hasFiles = (pendingFiles || []).length > 0;

    if ((sending) || (!text && !hasFiles)) return;

    setSending(true);
    setError("");

    try {
      // postPublicMessage din lib/chat ar trebui să suporte (token, text, {files})
      // dacă nu, facem fallback la text-only ca să nu "blocăm" complet.
      let msg = null;
      try {
        msg = unwrap(await postPublicMessage(token, text, { files: pendingFiles }));
      } catch (err) {
        if (hasFiles) throw err;
        msg = unwrap(await postPublicMessage(token, text));
      }

      if (msg && typeof msg === "object") {
        setMessages((prev) => [...prev, msg]);
      }

      setBody("");
      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setNewMsgCount(0);

      // după send, are sens să te ducă jos (că tu ai trimis)
      shouldAutoScrollRef.current = true;
      setTimeout(() => scrollToBottom("smooth"), 0);

      markPublicRead(token).catch(() => { });
    } catch {
      setError("Nu s-a putut trimite mesajul. Încearcă din nou.");
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="p-6 text-foreground">Se încarcă...</div>;
  if (error) return <div className="p-6 text-foreground">{error}</div>;
  if (!thread) return <div className="p-6 text-foreground">Chat indisponibil.</div>;


  const apptLabel = appt?.start_at ? new Date(appt.start_at).toLocaleString() : "-";
  const onlineLabel = thread?.is_active === false ? "Închis" : "Conectat";

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      <div className="max-w-2xl mx-auto p-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 mb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-semibold">{biz.name || "Chat"}</div>
              <div className="text-sm text-muted-foreground">Programare: {apptLabel}</div>
              <div className="text-sm text-muted-foreground">
                Specialist: {thread.staff?.name || "-"}
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-950/40 px-3 py-1 text-sm">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              {onlineLabel}
            </span>
          </div>
        </div>

        <div
          ref={listRef}
          onScroll={onScrollMessages}
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 h-[60vh] overflow-auto"
        >
          {messages.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Niciun mesaj încă. Scrie primul mesaj mai jos.
            </div>
          ) : (
            messages.map((m) => {
              const atts = Array.isArray(m?.attachments) ? m.attachments : [];
              const hasAtts = atts.length > 0;

              return (
                <div
                  key={m.id}
                  className={`mb-3 flex ${m.sender_type === "client" ? "justify-end" : "justify-start"
                    }`}
                >
                  <div
                    className={[
                      "max-w-[85%] rounded-2xl border px-3 py-2",
                      m.sender_type === "client"
                        ? "bg-slate-900 text-white border-slate-900/20 dark:bg-slate-50 dark:text-slate-900"
                        : "bg-slate-100 text-slate-900 border-slate-200 dark:bg-slate-800 dark:text-slate-50 dark:border-slate-700",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "text-xs mb-1 opacity-80",
                        m.sender_type === "client" ? "text-white/80 dark:text-slate-900/70" : "text-slate-500 dark:text-slate-300",
                      ].join(" ")}
                    >

                      {m.sender_name || (m.sender_type === "client" ? "Client" : "Clinician")}
                      {" • "}
                      {m.created_at ? new Date(m.created_at).toLocaleTimeString() : ""}
                    </div>

                    {/* text */}
                    {m.body ? (
                      <div className="whitespace-pre-wrap">{m.body}</div>
                    ) : null}

                    {/* attachments */}
                    {hasAtts ? (
                      <div className={`mt-2 grid gap-2 ${atts.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                        {atts.map((att) => {
                          const src = getAttUrl(att);
                          if (!src) return null;
                          return (
                            <a
                              key={att.id || src}
                              href={src}
                              target="_blank"
                              rel="noreferrer"
                              className="block overflow-hidden rounded-xl border border-border"
                              title="Deschide imaginea"
                            >
                              <img
                                src={src}
                                alt={att.original_name || "attachment"}
                                className="h-48 w-full object-cover"
                                loading="lazy"
                              />
                            </a>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}

          <div ref={bottomRef} />

          {newMsgCount > 0 && (
            <div className="sticky bottom-2 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  shouldAutoScrollRef.current = true;
                  setNewMsgCount(0);
                  scrollToBottom("smooth");
                }}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm shadow"
                title="Sari la ultimele mesaje"
              >
                {newMsgCount} mesaj(e) noi • Du-mă jos
              </button>
            </div>
          )}
        </div>

        {/* preview attachments before send */}
        {previews.length > 0 && (
          <div className="mt-3 rounded-xl border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground mb-2">
              Atașamente ({previews.length}/5)
            </div>
            <div className="grid grid-cols-4 gap-2">
              {previews.map((p, idx) => (
                <div key={p.url} className="relative overflow-hidden rounded-lg border border-border">
                  <img src={p.url} alt={p.name} className="h-20 w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePendingFile(idx)}
                    className="absolute top-1 right-1 rounded-md border border-border bg-background px-2 py-1 text-xs shadow"
                    title="Elimină"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={onSend} className="mt-3 flex gap-2 items-center">
          {/* hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            disabled={sending}
          />

          <button
            type="button"
            className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title="Atașează imagini"
          >
            📎
          </button>

          <input
            className="flex-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 placeholder:text-slate-400 dark:placeholder:text-slate-500 px-3 py-2"
            placeholder="Scrie un mesaj..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending}
          />

          <button
            className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2"
            disabled={sending || (!((body || "").trim()) && pendingFiles.length === 0)}
            type="submit"
          >
            {sending ? "Trimit..." : "Trimite"}
          </button>
        </form>

        {(biz.phone || biz.email || biz.address) && (
          <div className="mt-4 text-xs text-muted-foreground">
            {biz.phone ? <div>Telefon: {biz.phone}</div> : null}
            {biz.email ? <div>Email: {biz.email}</div> : null}
            {biz.address ? <div>Adresă: {biz.address}</div> : null}
          </div>
        )}
      </div>
    </div>
  );
}
